// Changed-slots delta for RSchema: which tables (and which of their slots)
// differ between two resolved schema states.
//
// The accumulator follows the RCap pattern: ingest collects the candidate
// tables touched by each walked entry, finalize resolves the views at start
// and end and diffs only those candidates. The RSchema DAG has no barriers,
// so this is the only place where `from`/`start` matters for RSchema.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { json } from "@hyper-hyper-space/hhs3_json";
import { walkDelta, computeForkMeet, } from "@hyper-hyper-space/hhs3_mvt";
export class RSchemaDelta {
    constructor(start, end, revisionBound, root) {
        this.start = start;
        this.end = end;
        this.revisionBound = revisionBound;
        this.type = root.type;
        this.changes = root.changes;
        this.nested = root.nested;
    }
    get tableChanges() { return this.changes.tableChanges; }
}
function sameLiteral(a, b) {
    if (a === undefined || b === undefined)
        return a === b;
    return json.toStringNormalized(a) === json.toStringNormalized(b);
}
function diffTable(table, before, after) {
    if (before === undefined && after === undefined)
        return undefined;
    const change = {
        table,
        existedBefore: before !== undefined,
        existsAfter: after !== undefined,
        columnChanges: [],
        concurrentDeletesChanged: false,
        fksChanged: false,
        restrictionsChanged: false,
    };
    if (before === undefined || after === undefined)
        return change;
    for (const column of new Set([...Object.keys(before.columns), ...Object.keys(after.columns)])) {
        if (!sameLiteral(before.columns[column], after.columns[column])) {
            change.columnChanges.push({ column, before: before.columns[column], after: after.columns[column] });
        }
    }
    change.concurrentDeletesChanged = before.concurrentDeletes !== after.concurrentDeletes;
    change.fksChanged = !sameLiteral(before.fks, after.fks);
    change.restrictionsChanged = !sameLiteral(before.restrictions, after.restrictions);
    if (change.columnChanges.length === 0 && !change.concurrentDeletesChanged
        && !change.fksChanged && !change.restrictionsChanged) {
        return undefined;
    }
    return change;
}
export class RSchemaDeltaAccumulator {
    constructor(schema, start, end) {
        this.schema = schema;
        this.start = start;
        this.end = end;
        this.candidateTables = new Set();
    }
    ingest(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = entry.payload;
            if (payload['action'] === 'create') {
                for (const def of payload.tables) {
                    this.candidateTables.add(def.name);
                }
                return true;
            }
            if (payload['action'] === 'schema-update') {
                for (const rule of payload.migration) {
                    this.candidateTables.add(rule.rule === 'add-table' ? rule.def.name : rule.table);
                }
                return true;
            }
            return false;
        });
    }
    finalize() {
        return __awaiter(this, void 0, void 0, function* () {
            const startView = yield this.schema.getView(this.start, this.start);
            const endView = yield this.schema.getView(this.end, this.end);
            const tableChanges = [];
            for (const table of this.candidateTables) {
                const change = diffTable(table, startView.getTable(table), endView.getTable(table));
                if (change !== undefined)
                    tableChanges.push(change);
            }
            return {
                type: this.schema.getType(),
                changes: { tableChanges },
                nested: new Map(),
            };
        });
    }
}
export function computeRSchemaDelta(schema, rawDag, start, end) {
    return __awaiter(this, void 0, void 0, function* () {
        const fork = yield rawDag.findForkPosition(start, end);
        if (fork.forkA.size > 0) {
            throw new Error("computeDelta requires END to extend START");
        }
        if (fork.forkB.size === 0) {
            const empty = {
                type: schema.getType(), changes: { tableChanges: [] }, nested: new Map(),
            };
            return new RSchemaDelta(start, end, fork.commonFrontier, empty);
        }
        const revisionBound = yield computeForkMeet(rawDag, fork.common);
        const root = yield walkDelta(rawDag, start, end, revisionBound, new RSchemaDeltaAccumulator(schema, start, end));
        return new RSchemaDelta(start, end, revisionBound, root);
    });
}
